/**
 * South African ID Number Validation
 * Adapted for the validation registry system
 */
import {
  registerValidation,
  type ValidationMeta,
  type ValidationResult,
} from './validation-registry';

export const zaIdValidationMeta: ValidationMeta = {
  type: 'id_number',
  name: 'South African ID Number',
  format: '13 digits (YYMMDDGGGSCAZ)',
  example: '8001015009087',
  description:
    'Validates South African ID numbers by checking length, date of birth, gender, citizenship, and checksum using the Luhn algorithm.',
  country: 'South Africa',
  category: 'Identity Documents',
};

export function validateSouthAfricanId(idNumber: string): ValidationResult {
  // assume everything is correct and if it later turns out not to be, just set this to false
  let correct: boolean = true;
  const error: string[] = [];

  //Ref: http://www.sadev.co.za/content/what-south-african-id-number-made
  // SA ID Number have to be 13 digits, so check the length
  if (idNumber.length !== 13 || !isNumber(idNumber)) {
    error.push(
      'ID number does not appear to be authentic - input not a valid number'
    );
    correct = false;
  }

  if (correct) {
    // get first 6 digits as a valid date
    const tempDate = new Date(
      parseInt(idNumber.substring(0, 2)),
      parseInt(idNumber.substring(2, 4)) - 1,
      parseInt(idNumber.substring(4, 6))
    );

    const id_date = tempDate.getDate();
    const id_month = tempDate.getMonth();
    const id_year = tempDate.getFullYear();

    const fullDate = id_date + '-' + (id_month + 1) + '-' + id_year;

    if (
      !(
        tempDate.getFullYear() % 100 === parseInt(idNumber.substring(0, 2)) &&
        id_month === parseInt(idNumber.substring(2, 4)) - 1 &&
        id_date === parseInt(idNumber.substring(4, 6))
      )
    ) {
      error.push(
        'ID number does not appear to be authentic - date part not valid'
      );
      correct = false;
    }

    if (correct) {
      // get the gender
      const genderCode = idNumber.substring(6, 10);
      const gender = parseInt(genderCode) < 5000 ? 'Female' : 'Male';

      // get country ID for citizenship
      const citizenship =
        parseInt(idNumber.substring(10, 11)) === 0 ? 'Yes' : 'No';

      // apply Luhn formula for check-digits
      let tempTotal = 0;
      let checkSum = 0;
      let multiplier = 1;
      for (let i = 0; i < 13; ++i) {
        tempTotal = parseInt(idNumber.charAt(i)) * multiplier;
        if (tempTotal > 9) {
          tempTotal =
            parseInt(tempTotal.toString().charAt(0)) +
            parseInt(tempTotal.toString().charAt(1));
        }
        checkSum = checkSum + tempTotal;
        multiplier = multiplier % 2 === 0 ? 1 : 2;
      }
      if (checkSum % 10 !== 0) {
        error.push(
          'ID number does not appear to be authentic - check digit is not valid'
        );
        correct = false;
      }

      // if no error found, return success with details
      if (correct) {
        return {
          isValid: true,
          message: `✅ Valid South African ID Number: ${idNumber}`,
          details: {
            birthDate: fullDate,
            gender: gender,
            southAfricanCitizen: citizenship,
            formatted: `${idNumber.substring(0, 6)}-${idNumber.substring(
              6,
              10
            )}-${idNumber.substring(10, 11)}-${idNumber.substring(11, 13)}`,
          },
        };
      }
    }
  }

  // otherwise, show the error
  return {
    isValid: false,
    message: `❌ ${error.join('; ')}`,
  };
}

function isNumber(n: string): boolean {
  return !isNaN(parseFloat(n)) && isFinite(Number(n));
}

// Test strings for the validation
export const zaIdTestStrings = [
  '8001015009087', // Valid ID
  '9405230077085', // Valid ID
  '0203275432012', // Valid ID
  '1234567890123', // Invalid checksum
  '9913015009087', // Invalid date
  '123456789', // Too short
  '80010150090871', // Too long
  'abc1234567890', // Contains letters
  '0000000000000', // All zeros
  '',
];

// Register the validation function
registerValidation(
  'za-id-number',
  zaIdValidationMeta,
  validateSouthAfricanId,
  zaIdTestStrings
);
